import React, { useEffect, useMemo } from "react";
import { Stack, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AutoModeIcon from "@mui/icons-material/AutoMode";
import SaveIcon from "@mui/icons-material/Save";

import FormAlert from "../components/common/FormAlert";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { startTimetableGeneration } from "../redux/actions/timetableJobActions";
import { updateTimetable } from "../redux/actions/timetableActions";
import { getTerms } from "../redux/actions/termActions";
import { getBranches } from "../redux/actions/branchActions";
import { timetableActions } from "../redux/slices/timetable";
import { timetableJobActions } from "../redux/slices/timetableJob";

import { TimetableStatus } from "../shared/enums/TimetableStatus";
import { TimetableJobRequestDto } from "../shared/dtos/timetableJob/TimetableJobRequestDto";
import { TimetableRequestDto } from "../shared/dtos/timetable/TimetableRequestDto";
import { TimetableResponseDto } from "../shared/dtos/timetable/TimetableResponseDto";

const createSchema = Yup.object().shape({
  branchId: Yup.string().required("Branch is required"),
  termId: Yup.string().required("Term is required"),
});

const updateSchema = Yup.object().shape({
  status: Yup.mixed<TimetableStatus>()
    .oneOf(Object.values(TimetableStatus))
    .required("Status is required"),
});

const statusOptions = [
  { value: TimetableStatus.INACTIVE, label: "Inactive (Draft)" },
  { value: TimetableStatus.ACTIVE, label: "Active (Published)" },
  { value: TimetableStatus.ARCHIVED, label: "Archived" },
];

interface CreateOrUpdateTimetableFormProps {
  timetable?: TimetableResponseDto | null;
  userInfo?: any;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

const CreateOrUpdateTimetableForm: React.FC<
  CreateOrUpdateTimetableFormProps
> = ({
  timetable = null,
  userInfo = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  // Selectors for Dropdowns (Create Mode Only)
  const { items: branches } = useAppSelector((state) => state.branch);
  const { items: terms } = useAppSelector((state) => state.term);

  const isSuperAdmin = userInfo?.userRole === "SUPER_ADMIN";

  // Fetch Dependencies
  useEffect(() => {
    if (!isEditing) {
      if (isSuperAdmin && (!branches || branches.length === 0)) {
        dispatch(getBranches());
      }
      if (!terms || terms.length === 0) {
        dispatch(getTerms({ activeOnly: true }));
      }
    }
  }, [dispatch, isEditing, isSuperAdmin, branches, terms]);

  // Options
  const branchOptions = useMemo(
    () => branches?.map((b) => ({ value: b.id, label: b.name })) || [],
    [branches],
  );
  const termOptions = useMemo(
    () => terms?.map((t) => ({ value: t.id, label: t.name })) || [],
    [terms],
  );

  const initialValues = isEditing
    ? {
        status: timetable?.status || TimetableStatus.INACTIVE,
      }
    : {
        branchId: isSuperAdmin ? "" : userInfo?.branchId || "",
        termId: "",
      };

  return (
    <Box sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(timetableActions.setErrorUpdatingTimetable(null))
            : () => dispatch(timetableJobActions.setErrorStartingJob(null))
        }
      />
      <Formik
        initialValues={initialValues}
        validationSchema={isEditing ? updateSchema : createSchema}
        onSubmit={(values) => {
          if (isEditing && timetable) {
            const updatePayload: TimetableRequestDto = {
              status: values.status as TimetableStatus,
            };
            dispatch(
              updateTimetable({ id: timetable.id, values: updatePayload }),
            );
          } else {
            const createPayload: TimetableJobRequestDto = {
              branchId: (values as any).branchId,
              termId: (values as any).termId,
            };
            dispatch(startTimetableGeneration(createPayload));
          }
        }}
        enableReinitialize
      >
        {({ dirty, isValid }: FormikProps<any>) => (
          <Form>
            <Stack spacing={2}>
              {!isEditing ? (
                <>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "info.main",
                      color: "info.contrastText",
                      borderRadius: 1,
                      textAlign: "left",
                    }}
                  >
                    This will start an Auto System optimization job. Existing
                    timetables for this Term/Branch combination may be affected.
                  </Box>

                  {isSuperAdmin ? (
                    <FormSelectField
                      name="branchId"
                      label="Branch"
                      options={branchOptions}
                    />
                  ) : (
                    <input
                      type="hidden"
                      name="branchId"
                      value={(initialValues as any).branchId}
                    />
                  )}

                  <FormSelectField
                    name="termId"
                    label="Academic Term"
                    options={termOptions}
                  />
                </>
              ) : (
                <FormSelectField
                  name="status"
                  label="Timetable Status"
                  options={statusOptions}
                />
              )}

              <SubmitButton
                title={isEditing ? "Update Status" : "Start Generation"}
                loading={loading}
                loadingText={isEditing ? "Updating..." : "Queueing..."}
                disabled={!dirty || !isValid || loading}
                startIcon={isEditing ? <SaveIcon /> : <AutoModeIcon />}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateOrUpdateTimetableForm;
